import { PublicCopy, PublicLanguage } from './types';

export const publicCopy: Record<PublicLanguage, PublicCopy> = {
  en: {
    navigation: {
      home: 'Home',
      inventory: 'Inventory',
      blog: 'Blog',
      financing: 'Financing',
      contact: 'Contact',
      callNow: 'Call Now',
      languageLabel: 'Language',
      myAccount: 'My BHPH Account',
    },
    common: {
      loadingWebsite: 'Loading website...',
      websiteNotAvailable: 'Website Not Available',
      websiteSettingsMissing: 'Website settings not configured.',
    },
    pages: {
      home: {
        title: 'Get Approved. Drive Today.',
        subtitle: 'Bad credit? No credit? Repo? We say YES. Starting at $1,000 down.',
        ctaInventory: 'See All Vehicles',
        ctaApply: 'Apply Online',
        featuredTitle: 'Available Now',
        detailCta: 'View Details',
        noVehicles: 'Inventory loading — call us for current availability.',
      },
      homeExtended: {
        ticker: [
          '✅ No Credit Check Required',
          '🚗 Drive Today with $1,000 Down',
          '✅ Bad Credit? Repo? Bankruptcy? — APPROVED',
          '📞 Call Now: {{phone}}',
          '✅ $175 Every 2 Weeks · 90-Day Warranty',
          '🚗 In-House Financing — We Say YES',
          '💬 Hablamos Español',
        ],
        approvalBadges: ['Bad Credit', 'No Credit', 'Repossession', 'Bankruptcy', 'ITIN'],
        vehiclesAvailable: (n: number) => `${n} Vehicles Available Now`,
        heroHeadline: ['Get Approved.', 'Drive Today.'],
        heroSub: ['Bad credit? No credit? Repo? ', 'We say YES.', ' Starting at $1,000 down.'],
        paymentLine: [
          'Starting at ',
          '$1,000 down',
          ' — payments as low as ',
          '$175 every 2 weeks.',
        ],
        socialProof: '⭐⭐⭐⭐⭐ Trusted by families · Hablamos Español',
        howItWorksSub: 'How It Works',
        bhphTitle: 'Why BHPH Is Different',
        bhphDesc:
          "We finance you directly — no bank, no credit bureau, no rejection letters. If you have a job, you're already halfway there.",
        cards: [
          {
            emoji: '🚫',
            title: 'No Credit Check',
            items: [
              'Bad credit approved',
              'No credit — no problem',
              'Repo / Bankruptcy OK',
              'ITIN accepted',
              'We verify income, not score',
            ],
          },
          {
            emoji: '💵',
            title: 'Low Down Payment',
            items: [
              'Starting at $1,000 down',
              '$175 every 2 weeks',
              'Flexible payment schedule',
              'Prices $2k – $15k range',
              'No hidden fees',
            ],
          },
          {
            emoji: '🔑',
            title: 'Drive Today',
            items: [
              'Same day approval',
              'Drive off the lot today',
              '90-day warranty included',
              'Clean titles, inspected cars',
              'Spanish-speaking staff',
            ],
          },
        ],
        stepsTitle: 'Drive Off the Lot in 3 Steps',
        stepsSub: 'Same day. Really.',
        steps: [
          {
            step: '1',
            title: 'Call or Apply',
            body: 'Call {{phone}} or submit our 2-minute online form. No SSN needed to start.',
          },
          {
            step: '2',
            title: 'Get Approved',
            body: 'We verify your income (not your credit score). Most customers are approved within 30 minutes.',
          },
          {
            step: '3',
            title: 'Drive Today',
            body: 'Pick your car, make your down payment, and drive home today with your 90-day warranty.',
          },
        ],
        stepsBtn: '📞 Start Now — {{phone}}',
        ctaBannerTitle: 'Ready to Get Approved?',
        ctaBannerSub: 'Takes 2 minutes. No impact on your credit. Get a same-day decision.',
        ctaBannerApply: '📋 Apply Online — Free ›',
        ctaBannerFooter: 'Se Habla Español',
        whyTitle: 'Why {{dealership}}?',
        stats: [
          { value: '✓', label: 'Families Served' },
          { value: '⭐', label: 'Top Rated' },
          { value: '90 Day', label: 'Warranty' },
        ],
        whyBullets: [
          'In-house financing — no third-party bank required',
          'Bilingual team — Hablamos Español',
          '90-day warranty on every vehicle',
          'Same-day approval and same-day delivery',
          'Serving families in our community',
        ],
        whyBtn: '📞 Call Now — {{phone}}',
        reviews: [
          {
            name: 'Maria G.',
            rating: 5,
            text: '"I had a repo on my credit and they still got me approved the same day. My kids and I drove home in a great car. God bless this place!"',
          },
          {
            name: 'Carlos R.',
            rating: 5,
            text: '"Vine sin crédito y me ayudaron a conseguir un carro ese mismo día. El proceso fue fácil y rápido. Los recomiendo 100%."',
          },
          {
            name: 'Tamara W.',
            rating: 5,
            text: '"After bankruptcy I thought no one would help me. They said yes immediately. Amazing experience, very professional."',
          },
        ],
      },
      inventory: {
        title: 'Find Your Next Vehicle',
        searchPlaceholder: 'Search by year, make, or model',
        make: 'Make',
        bodyStyle: 'Body Style',
        maxPrice: 'Max Price',
        sortBy: 'Sort By',
        sortNewest: 'Newest First',
        sortOldest: 'Oldest First',
        sortPriceLow: 'Price: Low to High',
        sortPriceHigh: 'Price: High to Low',
        reset: 'Reset',
        allMakes: 'All Makes',
        allBodyStyles: 'All Body Styles',
        emptyState: 'No vehicles match your filters.',
        detailCta: 'View Details',
      },
      vehicleDetail: {
        title: 'Vehicle Details',
        ctaCall: 'Call',
        ctaApply: 'Apply Now',
        backToInventory: 'Back to Inventory',
        notFound: 'Vehicle not found.',
        disclaimer: 'Price excludes taxes, title, and dealer fees.',
        vin: 'VIN',
        mileage: 'Mileage',
        transmission: 'Transmission',
        fuel: 'Fuel',
        color: 'Color',
        body: 'Body Style',
        na: 'N/A',
        contactFormTitle: 'Interested in this vehicle?',
        contactName: 'Name',
        contactPhoneLabel: 'Phone',
        contactEmailLabel: 'Email (Optional)',
        contactMessage: 'What are you interested in?',
        contactMessageDefault: (vehicleLabel: string) =>
          `Hi, I'm interested in the ${vehicleLabel}.`,
        contactSubmit: 'Send',
        contactSubmitting: 'Sending…',
        contactSuccess: "Thanks! We'll be in touch shortly.",
        contactError: (phone: string) =>
          `Couldn't submit that automatically — please call us at ${phone}.`,
        contactNameRequired: 'Name is required',
        contactPhoneInvalid: 'Enter a 10-digit phone number',
        contactEmailInvalid: 'Enter a valid email address',
        contactMessageTooLong: 'Message is too long',
      },
      financing: {
        title: 'Financing for Every Credit Situation',
        body: 'We work with many credit profiles and help you get approved quickly.',
        primaryCta: 'Start Credit Application',
        secondaryCta: 'Browse Inventory',
        fullName: 'Full Name',
        phone: 'Phone Number',
        email: 'Email (Optional)',
        message: 'Tell us what payment range works for you',
        submit: 'Submit Credit Request',
        success: 'Thank you. A finance specialist will contact you shortly.',
        submitError: 'Unable to submit right now. Please call us directly.',
        startApplication: 'Start Credit Application',
        wizard: {
          title: 'Credit Application',
          step1Title: 'Personal Information',
          step2Title: 'Financial Information',
          step3Title: 'Review & Submit',
          firstName: 'First Name',
          lastName: 'Last Name',
          phone: 'Phone Number',
          email: 'Email Address',
          dateOfBirth: 'Date of Birth',
          ssnLast4: 'Last 4 of SSN',
          monthlyIncome: 'Monthly Income',
          employmentStatus: 'Employment Status',
          employerName: 'Employer Name',
          downPayment: 'Down Payment Available',
          desiredPayment: 'Desired Monthly Payment',
          employed: 'Employed',
          selfEmployed: 'Self-Employed',
          retired: 'Retired',
          unemployed: 'Unemployed',
          vehicleInterest: 'Vehicle of Interest',
          selectVehicle: 'Select a vehicle (optional)',
          notes: 'Additional Notes',
          notesPlaceholder: 'Tell us about your situation or which vehicle interests you...',
          next: 'Next',
          back: 'Back',
          submit: 'Submit Application',
          submitting: 'Submitting...',
          successTitle: 'Application Submitted!',
          successMessage:
            'Thank you! We will review your application and contact you within 24 hours.',
          errorMessage: 'Something went wrong. Please try again.',
          required: 'This field is required',
        },
      },
      contact: {
        title: 'Contact Us',
        fullName: 'Full Name',
        phone: 'Phone Number',
        email: 'Email (Optional)',
        message: 'Message',
        submit: 'Send Message',
        success: 'Thank you. We’ll contact you shortly.',
        submitError: 'Unable to submit right now. Please call us directly.',
      },
    },
    foundation: {
      home: {
        title: 'Drive Home Today',
        description:
          'Modern bilingual foundation is active. Full Home experience is next in Phase 2.',
      },
      inventory: {
        title: 'Find Your Next Vehicle',
        description: 'Inventory page foundation is ready. Filters and search land in Phase 2.',
      },
      financing: {
        title: 'Financing for Every Credit Situation',
        description: 'Financing page foundation is ready. Credit app flow lands in Phase 2.',
      },
      contact: {
        title: 'Contact Us',
        description: 'Contact page foundation is ready. Lead form lands in Phase 2.',
      },
    },
  },
  es: {
    navigation: {
      home: 'Inicio',
      inventory: 'Inventario',
      blog: 'Blog',
      financing: 'Financiamiento',
      contact: 'Contacto',
      callNow: 'Llamar Ahora',
      languageLabel: 'Idioma',
      myAccount: 'Mi Cuenta BHPH',
    },
    common: {
      loadingWebsite: 'Cargando sitio web...',
      websiteNotAvailable: 'Sitio Web No Disponible',
      websiteSettingsMissing: 'La configuración del sitio web no está lista.',
    },
    pages: {
      home: {
        title: '¡Aprobado. Maneja Hoy!',
        subtitle:
          '¿Mal crédito? ¿Sin crédito? ¿Repossession? Decimos SÍ. Desde $1,000 de enganche.',
        ctaInventory: 'Ver Todos los Autos',
        ctaApply: 'Aplicar En Línea',
        featuredTitle: 'Disponibles Ahora',
        detailCta: 'Ver Detalles',
        noVehicles: 'Inventario cargando — llámanos para disponibilidad.',
      },
      homeExtended: {
        ticker: [
          '✅ No Se Requiere Verificación de Crédito',
          '🚗 Maneja Hoy con Solo $1,000 de Enganche',
          '✅ ¿Mal crédito? ¿Repo? ¿Bancarrota? — APROBADO',
          '📞 Llama Ahora: {{phone}}',
          '✅ $175 Cada 2 Semanas · Garantía de 90 Días',
          '🚗 Financiamiento Propio — Decimos SÍ',
          '💬 Hablamos Español',
        ],
        approvalBadges: ['Mal Crédito', 'Sin Crédito', 'Repossession', 'Bancarrota', 'ITIN'],
        vehiclesAvailable: (n: number) => `${n} Vehículos Disponibles Ahora`,
        heroHeadline: ['¡Aprobado.', 'Maneja Hoy!'],
        heroSub: [
          '¿Mal crédito? ¿Sin crédito? ¿Repossession? ',
          'Decimos SÍ.',
          ' Desde $1,000 de enganche.',
        ],
        paymentLine: ['Desde ', '$1,000 de enganche', ' — pagos desde ', '$175 cada 2 semanas.'],
        socialProof: '⭐⭐⭐⭐⭐ Familias confían en nosotros · Hablamos Español',
        howItWorksSub: 'Cómo Funciona',
        bhphTitle: 'Por Qué BHPH es Diferente',
        bhphDesc:
          'Te financiamos directamente — sin banco, sin buró de crédito, sin cartas de rechazo. Si tienes trabajo, ya estás a medio camino.',
        cards: [
          {
            emoji: '🚫',
            title: 'Sin Verificación de Crédito',
            items: [
              'Mal crédito aprobado',
              'Sin crédito — no hay problema',
              'Repossession / Bancarrota OK',
              'ITIN aceptado',
              'Verificamos ingresos, no puntaje',
            ],
          },
          {
            emoji: '💵',
            title: 'Enganche Bajo',
            items: [
              'Desde $1,000 de enganche',
              '$175 cada 2 semanas',
              'Pagos flexibles',
              'Precios de $2k a $15k',
              'Sin cargos ocultos',
            ],
          },
          {
            emoji: '🔑',
            title: 'Maneja Hoy',
            items: [
              'Aprobación el mismo día',
              'Maneja del lote hoy',
              'Garantía de 90 días incluida',
              'Títulos limpios, carros inspeccionados',
              'Personal bilingüe',
            ],
          },
        ],
        stepsTitle: 'Maneja del Lote en 3 Pasos',
        stepsSub: 'El mismo día. De verdad.',
        steps: [
          {
            step: '1',
            title: 'Llama o Aplica',
            body: 'Llama al {{phone}} o envía nuestro formulario de 2 minutos. No necesitas SSN para comenzar.',
          },
          {
            step: '2',
            title: 'Obtén Aprobación',
            body: 'Verificamos tus ingresos (no tu crédito). La mayoría de los clientes se aprueba en 30 minutos.',
          },
          {
            step: '3',
            title: 'Maneja Hoy',
            body: 'Elige tu carro, haz tu enganche y maneja a casa hoy con tu garantía de 90 días.',
          },
        ],
        stepsBtn: '📞 Empieza Ahora — {{phone}}',
        ctaBannerTitle: '¿Listo Para Ser Aprobado?',
        ctaBannerSub: 'Toma 2 minutos. Sin impacto en tu crédito. Obtén una decisión el mismo día.',
        ctaBannerApply: '📋 Aplica En Línea — Gratis ›',
        ctaBannerFooter: 'Se Habla Español',
        whyTitle: '¿Por Qué {{dealership}}?',
        stats: [
          { value: '✓', label: 'Familias Atendidas' },
          { value: '⭐', label: 'Mejor Calificados' },
          { value: '90 Días', label: 'Garantía' },
        ],
        whyBullets: [
          'Financiamiento propio — sin banco de terceros',
          'Equipo bilingüe — Hablamos Español',
          'Garantía de 90 días en cada vehículo',
          'Aprobación y entrega el mismo día',
          'Sirviendo a familias en nuestra comunidad',
        ],
        whyBtn: '📞 Llama Ahora — {{phone}}',
        reviews: [
          {
            name: 'Maria G.',
            rating: 5,
            text: '"Tenía un repossession en mi crédito y me aprobaron el mismo día. Mis hijos y yo manejamos a casa en un gran carro. ¡Dios bendiga este lugar!"',
          },
          {
            name: 'Carlos R.',
            rating: 5,
            text: '"Vine sin crédito y me ayudaron a conseguir un carro ese mismo día. El proceso fue fácil y rápido. Los recomiendo 100%."',
          },
          {
            name: 'Tamara W.',
            rating: 5,
            text: '"Después de la bancarrota pensé que nadie me ayudaría. Dijeron que sí de inmediato. Experiencia increíble, muy profesionales."',
          },
        ],
      },
      inventory: {
        title: 'Encuentra Tu Próximo Vehículo',
        searchPlaceholder: 'Busca por año, marca o modelo',
        make: 'Marca',
        bodyStyle: 'Carrocería',
        maxPrice: 'Precio Máximo',
        sortBy: 'Ordenar Por',
        sortNewest: 'Más Nuevo Primero',
        sortOldest: 'Más Antiguo Primero',
        sortPriceLow: 'Precio: Menor a Mayor',
        sortPriceHigh: 'Precio: Mayor a Menor',
        reset: 'Limpiar',
        allMakes: 'Todas las Marcas',
        allBodyStyles: 'Todos los Tipos',
        emptyState: 'No hay vehículos que coincidan con tus filtros.',
        detailCta: 'Ver Detalles',
      },
      vehicleDetail: {
        title: 'Detalles del Vehículo',
        ctaCall: 'Llamar',
        ctaApply: 'Aplicar Ahora',
        backToInventory: 'Volver a Inventario',
        notFound: 'Vehículo no encontrado.',
        disclaimer: 'El precio no incluye impuestos, título ni cargos del concesionario.',
        vin: 'VIN',
        mileage: 'Millaje',
        transmission: 'Transmisión',
        fuel: 'Combustible',
        color: 'Color',
        body: 'Carrocería',
        na: 'N/D',
        contactFormTitle: '¿Interesado en este vehículo?',
        contactName: 'Nombre',
        contactPhoneLabel: 'Teléfono',
        contactEmailLabel: 'Correo (Opcional)',
        contactMessage: '¿Qué te interesa?',
        contactMessageDefault: (vehicleLabel: string) =>
          `Hola, estoy interesado en el ${vehicleLabel}.`,
        contactSubmit: 'Enviar',
        contactSubmitting: 'Enviando…',
        contactSuccess: '¡Gracias! Nos pondremos en contacto pronto.',
        contactError: (phone: string) =>
          `No se pudo enviar automáticamente — por favor llámanos al ${phone}.`,
        contactNameRequired: 'El nombre es obligatorio',
        contactPhoneInvalid: 'Ingresa un número de teléfono de 10 dígitos',
        contactEmailInvalid: 'Ingresa un correo electrónico válido',
        contactMessageTooLong: 'El mensaje es demasiado largo',
      },
      financing: {
        title: 'Financiamiento para Toda Situación de Crédito',
        body: 'Trabajamos con muchos perfiles de crédito y te ayudamos a aprobar rápidamente.',
        primaryCta: 'Iniciar Solicitud de Crédito',
        secondaryCta: 'Ver Inventario',
        fullName: 'Nombre Completo',
        phone: 'Número de Teléfono',
        email: 'Correo (Opcional)',
        message: 'Cuéntanos qué rango de pago te funciona',
        submit: 'Enviar Solicitud de Crédito',
        success: 'Gracias. Un especialista en financiamiento te contactará pronto.',
        submitError: 'No se pudo enviar ahora. Por favor llámanos directamente.',
        startApplication: 'Iniciar Solicitud de Credito',
        wizard: {
          title: 'Solicitud de Credito',
          step1Title: 'Informacion Personal',
          step2Title: 'Informacion Financiera',
          step3Title: 'Revisar y Enviar',
          firstName: 'Nombre',
          lastName: 'Apellido',
          phone: 'Telefono',
          email: 'Correo Electronico',
          dateOfBirth: 'Fecha de Nacimiento',
          ssnLast4: 'Ultimos 4 del SSN',
          monthlyIncome: 'Ingreso Mensual',
          employmentStatus: 'Estado de Empleo',
          employerName: 'Nombre del Empleador',
          downPayment: 'Pago Inicial Disponible',
          desiredPayment: 'Pago Mensual Deseado',
          employed: 'Empleado',
          selfEmployed: 'Trabajador Independiente',
          retired: 'Jubilado',
          unemployed: 'Desempleado',
          vehicleInterest: 'Vehiculo de Interes',
          selectVehicle: 'Seleccione un vehiculo (opcional)',
          notes: 'Notas Adicionales',
          notesPlaceholder: 'Cuentenos sobre su situacion o que vehiculo le interesa...',
          next: 'Siguiente',
          back: 'Atras',
          submit: 'Enviar Solicitud',
          submitting: 'Enviando...',
          successTitle: 'Solicitud Enviada!',
          successMessage:
            'Gracias! Revisaremos su solicitud y lo contactaremos dentro de 24 horas.',
          errorMessage: 'Algo salio mal. Por favor intente de nuevo.',
          required: 'Este campo es requerido',
        },
      },
      contact: {
        title: 'Contáctanos',
        fullName: 'Nombre Completo',
        phone: 'Número de Teléfono',
        email: 'Correo (Opcional)',
        message: 'Mensaje',
        submit: 'Enviar Mensaje',
        success: 'Gracias. Te contactaremos pronto.',
        submitError: 'No se pudo enviar ahora. Por favor llámanos directamente.',
      },
    },
    foundation: {
      home: {
        title: 'Maneja Tu Auto Hoy',
        description:
          'La base moderna bilingüe está activa. La experiencia completa llega en la Fase 2.',
      },
      inventory: {
        title: 'Encuentra Tu Próximo Vehículo',
        description: 'La base de Inventario está lista. Filtros y búsqueda llegan en la Fase 2.',
      },
      financing: {
        title: 'Financiamiento para Toda Situación de Crédito',
        description:
          'La base de Financiamiento está lista. La solicitud de crédito llega en la Fase 2.',
      },
      contact: {
        title: 'Contáctanos',
        description: 'La base de Contacto está lista. El formulario llega en la Fase 2.',
      },
    },
  },
};
